/*
  Paco & Pepper – accessibility enhancements (WCAG 2.2 AA)
  Loaded by every layout (deferred). Pairs with pp-a11y.css.

  This file is progressive enhancement only: it never changes which variant,
  selling plan or cart line is submitted, and it never calls into third-party
  internals. Every behaviour below routes user intent through the existing
  GemPages / Kaching / Skio event paths so the apps stay the source of truth.

  Findings covered:
    F01  variant radios: keyboard (arrow keys / Space) selects the same variant
         as a click, and the checked state always mirrors the visible selection
    F02  Kaching cart drawer: closed drawer is inert (no tab stops), open drawer
         keeps Tab/Shift+Tab inside, Escape closes, focus returns to the opener
    F03  FAQ accordions: role=button, tabindex, aria-expanded/controls, Enter/Space
    F07  GemPages carousel dots: "Go to slide N of M" + aria-current
    F14  motion preference detection (prefers-reduced-motion + "Stop animations")
*/
(function () {
  'use strict';

  var doc = document;
  var FOCUSABLE =
    'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),' +
    'select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';
  var uidCounter = 0;

  function uid(prefix) {
    uidCounter += 1;
    return (prefix || 'pp-a11y') + '-' + uidCounter;
  }

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    if (el.closest('[inert]')) return false;
    var style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function onReady(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ------------------------------------------------------------------ */
  /* F01 – variant radios                                                */
  /* ------------------------------------------------------------------ */

  // `notify`: when the sync itself flips a radio on, dispatch the bubbling
  // `change` the browser would have fired. Needed because this runs from a
  // MutationObserver *between* GemPages' click handler (which toggles the
  // class) and the browser's own radio activation – by then the radio is
  // already checked, so the native click produces no `change`, and form-level
  // listeners (Skio's plan picker prices, for one) would never hear about it.
  function syncRadiosFromActive(picker, notify) {
    var items = picker.querySelectorAll('.option-item');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var input = item.querySelector('input[type="radio"]');
      if (!input) continue;
      // Never leave `checked="false"` (or any stale default) in the DOM – the
      // attribute is boolean and its presence alone means "checked".
      if (input.hasAttribute('checked')) input.removeAttribute('checked');
      var active = item.classList.contains('option-item-active');
      if (active && !input.checked) {
        input.checked = true;
        if (notify) input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function initVariantPicker(picker) {
    if (picker.__ppA11y) return;
    picker.__ppA11y = true;

    // Give each option group a name ("Type", "Size") for AT users.
    var groups = picker.querySelectorAll('.variant-option-group');
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      if (!group.getAttribute('role')) group.setAttribute('role', 'radiogroup');
      var legend = group.querySelector('legend');
      if (legend && !group.getAttribute('aria-labelledby') && !group.getAttribute('aria-label')) {
        if (!legend.id) legend.id = uid('pp-variant-legend');
        group.setAttribute('aria-labelledby', legend.id);
      }
    }

    syncRadiosFromActive(picker);

    // Keyboard path: arrow keys / Space check the radio natively and fire
    // `change` on the input. GemPages only listens for `click` on the visible
    // card, so forward the intent to the card – the same code path a mouse
    // click uses – unless the card is already active (mouse path, which
    // already ran GemPages' handler before the browser toggled the radio).
    picker.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'radio') return;
      var item = input.closest('.option-item');
      if (!item || item.classList.contains('option-item-active')) return;
      var card = item.querySelector('.option-value-wrapper');
      if (!card) return;
      card.click();
      // GemPages has now written the new variant id into the form's hidden
      // [name="id"] input; the native `change` above fired *before* that, so
      // re-announce it for form-level listeners (Skio prices etc.).
      if (item.classList.contains('option-item-active')) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Mouse path (and GemPages' own initial highlight): keep radio.checked in
    // step with the `option-item-active` class GemPages toggles.
    var observer = new MutationObserver(function () {
      syncRadiosFromActive(picker, true);
    });
    observer.observe(picker, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  function enhanceVariantPickers(root) {
    var pickers = (root || doc).querySelectorAll('gp-product-variants');
    for (var i = 0; i < pickers.length; i++) initVariantPicker(pickers[i]);
  }

  // GemPages fires this after any variant change (click, dropdown, URL sync).
  window.addEventListener('gp:product-change-variant', function () {
    enhanceVariantPickers();
    var pickers = doc.querySelectorAll('gp-product-variants');
    for (var i = 0; i < pickers.length; i++) syncRadiosFromActive(pickers[i], true);
  });

  /* ------------------------------------------------------------------ */
  /* F03 – FAQ accordions (gp-accordion)                                 */
  /* ------------------------------------------------------------------ */

  function accordionBodyExpanded(body) {
    var mh = body.style.maxHeight;
    var rows = body.style.gridTemplateRows;
    if (mh === '0px') return false;
    if (rows === '0fr') return false;
    if (body.getAttribute('data-show') === 'false' && !mh) return false;
    return true;
  }

  function initAccordionItem(item) {
    if (item.__ppA11y) return;
    var header = item.querySelector('.gp-accordion-item_header');
    var body = item.querySelector('.gp-accordion-item_body');
    if (!header || !body) return;
    item.__ppA11y = true;

    if (!header.id) header.id = uid('pp-acc-header');
    if (!body.id) body.id = header.id + '-panel';

    header.removeAttribute('aria-hidden');
    header.setAttribute('role', 'button');
    header.setAttribute('tabindex', '0');
    header.setAttribute('aria-controls', body.id);
    body.setAttribute('role', 'region');
    body.setAttribute('aria-labelledby', header.id);

    var sync = function () {
      var expanded = accordionBodyExpanded(body);
      header.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (expanded) {
        body.removeAttribute('inert');
        body.removeAttribute('aria-hidden');
      } else {
        body.setAttribute('aria-hidden', 'true');
        body.setAttribute('inert', '');
      }
    };
    sync();
    new MutationObserver(sync).observe(body, {
      attributes: true,
      attributeFilter: ['style', 'data-show', 'class']
    });

    header.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        header.click();
      }
    });
  }

  function enhanceAccordions(root) {
    var items = (root || doc).querySelectorAll('gp-accordion .gp-accordion-item');
    for (var i = 0; i < items.length; i++) initAccordionItem(items[i]);
  }

  /* ------------------------------------------------------------------ */
  /* F07 – GemPages carousel dots (hero slider)                          */
  /* ------------------------------------------------------------------ */

  function labelCarouselDots(container) {
    var dots = container.querySelectorAll('button');
    for (var i = 0; i < dots.length; i++) {
      var dot = dots[i];
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + dots.length);
      if (dot.classList.contains('gem-slider-dot-active')) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  function enhanceCarouselDots(root) {
    var containers = (root || doc).querySelectorAll('.gp-carousel-dot-container');
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      if (container.__ppA11y) continue;
      container.__ppA11y = true;
      labelCarouselDots(container);
      // The runtime re-renders the dots (innerHTML) on every slide change.
      new MutationObserver(function (records) {
        labelCarouselDots(records[0].target.closest('.gp-carousel-dot-container') || container);
      }).observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  }

  /* ------------------------------------------------------------------ */
  /* F02 – Kaching cart drawer (app embed; internals live in the app)    */
  /* The app exposes data-open="true|false" on #kaching-cart. We only    */
  /* read that attribute and manage focus around it.                    */
  /* ------------------------------------------------------------------ */

  var kaching = { el: null, opener: null, wasOpen: null, attrObserver: null, reapplies: 0, lastTabFrom: null, lastTabShift: false };

  function kachingIsOpen(cart) {
    return cart.getAttribute('data-open') === 'true';
  }

  function kachingFocusables(cart) {
    var all = cart.querySelectorAll(FOCUSABLE);
    var list = [];
    for (var i = 0; i < all.length; i++) if (isVisible(all[i])) list.push(all[i]);
    return list;
  }

  function cleanAriaLabel(cart) {
    // The app sets aria-label="<h4>My cart • 0</h4>"; strip the markup so the
    // dialog name reads "My cart • 0".
    var label = cart.getAttribute('aria-label');
    if (label && label.indexOf('<') !== -1) {
      var tmp = doc.createElement('div');
      tmp.innerHTML = label;
      var text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) cart.setAttribute('aria-label', text);
    }
  }

  function applyKachingState(cart) {
    var open = kachingIsOpen(cart);
    cleanAriaLabel(cart);
    if (open === kaching.wasOpen) {
      // The app clears `inert` once after it finishes mounting; put it back
      // while the drawer is closed (bounded, so a disagreeing app cannot
      // cause a loop).
      if (!open && !cart.hasAttribute('inert') && kaching.reapplies < 10) {
        kaching.reapplies += 1;
        cart.setAttribute('inert', '');
      }
      return;
    }

    if (open) {
      cart.removeAttribute('inert');
      var active = doc.activeElement;
      if (active && active !== doc.body && !cart.contains(active)) kaching.opener = active;
      // The app focuses the close button itself after its open animation.
      // If it has not done so shortly after opening, move focus into the dialog.
      window.setTimeout(function () {
        if (!kachingIsOpen(cart)) return;
        if (cart.contains(doc.activeElement)) return;
        var first = cart.querySelector('.kaching-cart__close') || kachingFocusables(cart)[0];
        if (first) first.focus({ preventScroll: true });
      }, 450);
    } else {
      var ae = doc.activeElement;
      var focusWasInside = !ae || ae === doc.body || cart.contains(ae);
      cart.setAttribute('inert', '');
      if (kaching.wasOpen === true && focusWasInside && kaching.opener && kaching.opener.isConnected) {
        var opener = kaching.opener;
        window.setTimeout(function () {
          if (kachingIsOpen(cart)) return;
          if (doc.activeElement && doc.activeElement !== doc.body && !cart.contains(doc.activeElement)) return;
          opener.focus({ preventScroll: true });
        }, 0);
      }
    }
    kaching.wasOpen = open;
  }

  function initKaching() {
    var cart = doc.getElementById('kaching-cart');
    if (!cart || cart === kaching.el) return;
    if (cart.getAttribute('role') !== 'dialog') return;
    kaching.el = cart;
    kaching.wasOpen = null;
    kaching.reapplies = 0;
    applyKachingState(cart);
    if (kaching.attrObserver) kaching.attrObserver.disconnect();
    kaching.attrObserver = new MutationObserver(function () {
      applyKachingState(cart);
    });
    kaching.attrObserver.observe(cart, { attributes: true, attributeFilter: ['data-open', 'aria-label', 'inert'] });
  }

  // While the drawer is closed, remember what the user last focused so focus
  // can be restored even when the app opens the drawer asynchronously (e.g.
  // after the add-to-cart request resolves). While it is open, any focus that
  // lands outside the dialog is a leak (the app re-renders its header when the
  // item count changes, which resets the browser's Tab starting point) and is
  // redirected to the element the user was heading for.
  doc.addEventListener(
    'focusin',
    function (e) {
      var cart = kaching.el;
      if (cart && cart.contains(e.target)) return;
      if (!cart || !kachingIsOpen(cart)) {
        if (e.target && e.target !== doc.body) kaching.opener = e.target;
        return;
      }
      var focusables = kachingFocusables(cart);
      if (!focusables.length) return;
      var target = null;
      var idx = kaching.lastTabFrom ? focusables.indexOf(kaching.lastTabFrom) : -1;
      if (idx !== -1) target = focusables[(idx + (kaching.lastTabShift ? -1 : 1) + focusables.length) % focusables.length];
      if (!target) target = kaching.lastTabShift ? focusables[focusables.length - 1] : focusables[0];
      kaching.lastTabFrom = null;
      target.focus({ preventScroll: true });
    },
    true
  );

  doc.addEventListener(
    'keydown',
    function (e) {
      var cart = kaching.el;
      if (!cart || !kachingIsOpen(cart)) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        // Let the app handle Escape first; only use its own Close button if
        // the drawer is still open afterwards.
        window.setTimeout(function () {
          if (!kachingIsOpen(cart)) return;
          var close = cart.querySelector('.kaching-cart__close');
          if (close) close.click();
        }, 50);
        return;
      }

      if (e.key !== 'Tab') return;
      var focusables = kachingFocusables(cart);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = doc.activeElement;
      kaching.lastTabFrom = cart.contains(active) ? active : null;
      kaching.lastTabShift = !!e.shiftKey;
      if (!cart.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* F14 – motion preference (OS setting + overlay "Stop animations")    */
  /* Exposes window.ppA11y.motionReduced() and dispatches                */
  /* `pp:motion-change` on document whenever the answer changes.         */
  /* ------------------------------------------------------------------ */

  var motion = { probe: null, reduced: null, mq: null };
  var STOP_ANIM_RE = /stop[-_]?anim|anim[a-z]*[-_]?(stop|off|pause|disabled)|reduce[-_]?motion/i;

  function overlayStoppedAnimations() {
    var html = doc.documentElement;
    var body = doc.body;
    var haystack = [html.className, body ? body.className : ''];
    var attrs = html.attributes;
    for (var i = 0; i < attrs.length; i++) haystack.push(attrs[i].name + '=' + attrs[i].value);
    if (body) for (var j = 0; j < body.attributes.length; j++) haystack.push(body.attributes[j].name + '=' + body.attributes[j].value);
    if (STOP_ANIM_RE.test(haystack.join(' '))) return true;

    if (motion.probe && motion.probe.isConnected) {
      var cs = window.getComputedStyle(motion.probe);
      if (cs.animationName === 'none' || cs.animationPlayState === 'paused' || cs.animationDuration === '0s') return true;
    }
    return false;
  }

  function evaluateMotion() {
    var reduced = (motion.mq && motion.mq.matches) || overlayStoppedAnimations();
    if (reduced !== motion.reduced) {
      motion.reduced = reduced;
      doc.documentElement.classList.toggle('pp-a11y-motion-reduced', reduced);
      doc.dispatchEvent(new CustomEvent('pp:motion-change', { detail: { reduced: reduced } }));
    }
    return reduced;
  }

  function initMotion() {
    if (motion.probe) return;
    motion.mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    motion.probe = doc.createElement('div');
    motion.probe.className = 'pp-a11y-motion-probe';
    motion.probe.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(motion.probe);
    evaluateMotion();
    if (motion.mq) {
      if (motion.mq.addEventListener) motion.mq.addEventListener('change', evaluateMotion);
      else if (motion.mq.addListener) motion.mq.addListener(evaluateMotion);
    }
    new MutationObserver(evaluateMotion).observe(doc.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-acsb', 'data-acsb-active']
    });
    new MutationObserver(evaluateMotion).observe(doc.head, { childList: true });
    window.setInterval(evaluateMotion, 1500);
  }

  /* ------------------------------------------------------------------ */
  /* Durability shims – the same fixes exist in the Liquid, but GemPages */
  /* regenerates section files on republish. These keep F09 / F10 and   */
  /* the aria-label / newsletter advisories in place regardless.        */
  /* ------------------------------------------------------------------ */

  var LOGO_ALTS = {
    'tlog.png': 'Target',
    'shop-1.png': "Gelson's",
    'Screenshot_2025-11-01_at_7.36.10_PM.png': "Boney's Bayside Market",
    'download_4238005b-36e8-46a1-a3ae-b228a41fee0d.svg': 'Windmill Farms Community Market',
    'mothers.jpg': "Mother's California Market",
    'shop-3.png': 'Metropolitan Market',
    'shop-4.png': 'New Leaf Community Markets',
    'shop-2.png': 'Lazy Acres Natural Market',
    'logo_1553f92f-d9d4-4b63-a28b-c4a861631e7c.png': 'Meowtamins by Paco & Pepper'
  };
  var SOCIAL_NAMES = [
    [/tiktok\.com/i, 'Paco & Pepper on TikTok'],
    [/instagram\.com/i, 'Paco & Pepper on Instagram'],
    [/youtube\.com/i, 'Paco & Pepper on YouTube'],
    [/facebook\.com/i, 'Paco & Pepper on Facebook']
  ];

  function fileName(url) {
    var m = /\/([^\/?#]+)(?:[?#]|$)/.exec(url || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function enhanceContent(root) {
    var scope = root || doc;
    var i, el;

    // aria-label values that contain markup ("<p>Order Now</p>")
    var labelled = scope.querySelectorAll('.gps [aria-label*="<"], .gps[aria-label*="<"]');
    for (i = 0; i < labelled.length; i++) {
      el = labelled[i];
      var tmp = doc.createElement('div');
      tmp.innerHTML = el.getAttribute('aria-label');
      var text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) el.setAttribute('aria-label', text);
      else el.removeAttribute('aria-label');
    }

    // Newsletter email fields rendered by GemPages forms
    var emails = scope.querySelectorAll('.gps input[name="contact[email]"]:not([aria-label]):not([aria-labelledby])');
    for (i = 0; i < emails.length; i++) {
      if (!emails[i].id || !doc.querySelector('label[for="' + emails[i].id + '"]')) emails[i].setAttribute('aria-label', 'Email address');
    }

    // Icon-only social links
    var links = scope.querySelectorAll('.gps a[href]:not([aria-label])');
    for (i = 0; i < links.length; i++) {
      el = links[i];
      if ((el.textContent || '').trim() || (el.getAttribute('title') || '').trim()) continue;
      if (!el.querySelector('svg, img')) continue;
      for (var s = 0; s < SOCIAL_NAMES.length; s++) {
        if (SOCIAL_NAMES[s][0].test(el.href)) {
          el.setAttribute('aria-label', SOCIAL_NAMES[s][1]);
          break;
        }
      }
    }

    // Images without alt: known logos get their name, decorative helpers get alt=""
    var imgs = scope.querySelectorAll('.gps img:not([alt]), .gps-custom-code img:not([alt]), img.verified-icon:not([alt]), #store-locator-wrapper img:not([alt])');
    for (i = 0; i < imgs.length; i++) {
      el = imgs[i];
      var name = LOGO_ALTS[fileName(el.getAttribute('base-src') || el.getAttribute('data-src') || el.currentSrc || el.src)];
      if (name) el.setAttribute('alt', name);
      else if (el.classList.contains('verified-icon') || el.classList.contains('gp_lazyforbg') || el.closest('.gp-thumbnail-video, [role="presentation"]') || /^data:/.test(el.getAttribute('src') || '')) el.setAttribute('alt', '');
    }
    var namedImgs = scope.querySelectorAll('.gps img[alt="Alt image"], .gps img[alt="Alt Image"]');
    for (i = 0; i < namedImgs.length; i++) {
      el = namedImgs[i];
      var logo = LOGO_ALTS[fileName(el.getAttribute('base-src') || el.getAttribute('data-src') || el.currentSrc || el.src)];
      if (logo) el.setAttribute('alt', logo);
      else if (el.closest('.gp-thumbnail-video, [role="presentation"]')) el.setAttribute('alt', '');
    }

    // Decorative hero overlays (aria-label on a plain div is prohibited)
    var overlays = scope.querySelectorAll('.gps div[aria-label="Overlay"]');
    for (i = 0; i < overlays.length; i++) {
      overlays[i].removeAttribute('aria-label');
      overlays[i].setAttribute('aria-hidden', 'true');
    }
  }

  window.ppA11y = {
    motionReduced: function () {
      return motion.reduced === null ? evaluateMotion() : motion.reduced;
    },
    refresh: function (root) {
      enhanceVariantPickers(root);
      enhanceAccordions(root);
      enhanceCarouselDots(root);
      enhanceContent(root);
      initKaching();
    }
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  onReady(function () {
    window.ppA11y.refresh();
    initMotion();

    // GemPages renders some sections lazily and Kaching mounts after load:
    // pick up new nodes as they appear.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        window.ppA11y.refresh();
      });
    }).observe(doc.body, { childList: true, subtree: true });
  });
})();
